import type { StaffSessionDto } from '@planuze/admin-contracts';
import { SearchIcon } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useAdminContent } from '../content/locale-provider.js';
import { sectionsForRole } from '../lib/nav.js';
import { BrandLogo } from './brand-logo.js';
import { CommandPalette } from './command-palette.js';
import { LocaleToggle } from './locale-toggle.js';
import { SidebarNav } from './sidebar-nav.js';
import { ThemeToggle } from './theme-toggle.js';
import { UserMenu } from './user-menu.js';

interface AdminLayoutProps {
  readonly session: StaffSessionDto;
  readonly children: ReactNode;
}

/* Shell do backoffice (ADR-0700): sidebar (marca + navegação por seção) + header utilitário
   (busca/command-palette à esquerda ⌘K + tema/operador à direita) + área de conteúdo (slot nu —
   cada rota monta seu `<Page>`). */
export function AdminLayout({ session, children }: AdminLayoutProps) {
  const sections = sectionsForRole(session.role);
  const { shell } = useAdminContent();
  const [paletteOpen, setPaletteOpen] = useState(false);

  /* ⌘K / Ctrl+K abre o command palette de qualquer rota (§7.5). */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex h-dvh overflow-hidden bg-background text-foreground">
      <aside className="flex w-64 shrink-0 flex-col border-r border-border bg-card">
        <BrandLogo />
        <SidebarNav sections={sections} />
      </aside>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-border bg-card px-5">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex w-full max-w-xs items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-input hover:text-foreground"
          >
            <SearchIcon className="size-4 shrink-0" aria-hidden="true" />
            <span className="flex-1 text-left">{shell.searchPlaceholder}</span>
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              ⌘K
            </kbd>
          </button>
          <div className="flex shrink-0 items-center gap-1.5">
            <LocaleToggle />
            <ThemeToggle />
            <div className="mx-1 h-6 w-px bg-border" aria-hidden="true" />
            <UserMenu staff={session} />
          </div>
        </header>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{children}</div>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
